/**
 * Connection — vendor-agnostic shape consumed by surface-level pickers.
 *
 * A "connection" is one authenticated account the user has wired into Ando
 * (one Google Calendar token, one Outlook mailbox, one Drive workspace …).
 * Every workspace surface (Calendar, Mail, Drive, Finance, CRM, …) lists
 * its own kind via dedicated TanStack Query hooks (see
 * ``apps/client/src/features/workspace/hooks/useCalendar.ts``) — those hooks
 * already return rich, kind-specific rows.
 *
 * This module ports those rows down to the lowest-common-denominator that
 * the shared ``<ConnectionPicker />`` needs: id, provider, account label,
 * optional avatar / brand colour, kind tag. Feature code maps its
 * kind-specific connection (``CalendarConnection``, future
 * ``MailConnection`` …) into this shape on the way in.
 *
 * Per CLAUDE.md "white-label naming OBBLIGATORIO" — ``provider`` is opaque
 * to the picker. Callers stay free to pass a vendor slug (``google``,
 * ``microsoft``, ``apple``, ``salesforce`` …) OR a white-labelled capability
 * vendor (``acube``, ``aruba_pec`` …) — the colour map covers the difference.
 */

import { z } from "zod";

// ---------------------------------------------------------------------------
// Surface kind — one slug per workspace hub. Used both as ``surfaceId`` for
// ``useActiveConnections`` (localStorage namespacing) and as a discriminator
// callers can switch on when composing UI.
// ---------------------------------------------------------------------------

export const ConnectionKindSchema = z.enum([
  "calendar",
  "mail",
  "drive",
  "finance",
  "crm",
]);
export type ConnectionKind = z.infer<typeof ConnectionKindSchema>;

// ---------------------------------------------------------------------------
// Provider — kept ``string`` deliberately so callers can extend without
// editing this file. The picker treats unknown providers as "custom" and
// falls back to the Ando accent colour.
// ---------------------------------------------------------------------------

export type ConnectionProvider =
  | "google"
  | "microsoft"
  | "apple"
  | "salesforce"
  | (string & {});

export const ConnectionSchema = z.object({
  /** Stable identifier — used as React key and localStorage entry. */
  id: z.string().min(1),
  /** Vendor / brand slug — drives the default colour + avatar fallback. */
  provider: z.string().min(1),
  /** Human-readable account label — e.g. ``"andrea.bovinelli@gmail.com"``. */
  account_label: z.string().min(1),
  /** Optional avatar URL. When absent the picker renders a provider mark. */
  avatar_url: z.string().url().optional(),
  /** Optional per-connection brand override (e.g. ``"#1A73E8"``). */
  color_hex: z.string().optional(),
  /** Surface this connection belongs to. */
  kind: ConnectionKindSchema,
});

export interface Connection {
  /** Stable identifier — used as React key and localStorage entry. */
  readonly id: string;
  /** Vendor / brand slug. */
  readonly provider: ConnectionProvider;
  /** Human-readable account label — usually the e-mail address. */
  readonly account_label: string;
  /** Optional avatar URL. */
  readonly avatar_url?: string;
  /** Optional brand colour override (CSS hex / rgb). */
  readonly color_hex?: string;
  /** Surface this connection belongs to. */
  readonly kind: ConnectionKind;
}
